import { CustomOperatorValue } from '@aline/common';
import type { GridFilterItem, GridFilterModel, GridSortModel } from '@mui/x-data-grid-premium';
import { Apollo } from 'src/gql/generated';

export const WORKFLOW_LIST_PRESET_IDS = [
  'open',
  'requested-by-me',
  'assigned-to-me',
  'completed',
  'all',
] as const;

export type WorkflowListPresetId = (typeof WORKFLOW_LIST_PRESET_IDS)[number];

export const DEFAULT_WORKFLOW_LIST_PRESET: WorkflowListPresetId = 'open';

/** Minimal user shape stored in owner / assignee filter values. */
export interface WorkflowListPresetUser {
  __typename: 'User';
  displayName: string;
  email: string;
  id: string;
}

export interface WorkflowListPresetDefinition {
  id: WorkflowListPresetId;
  label: string;
}

export const WORKFLOW_LIST_PRESET_DEFINITIONS = {
  all: { id: 'all', label: 'All workflows' },
  'assigned-to-me': { id: 'assigned-to-me', label: 'Assigned to me' },
  completed: { id: 'completed', label: 'Completed' },
  open: { id: 'open', label: 'Open' },
  'requested-by-me': { id: 'requested-by-me', label: 'Requested by me' },
} satisfies Record<WorkflowListPresetId, WorkflowListPresetDefinition>;

/** Stable filter-item ids. */
export const WORKFLOW_LIST_PRESET_FILTER_ITEM_ID = {
  assignedToMePermissions: 'view:assigned-to-me:permissions',
  completedColumnCategory: 'view:completed:column-category',
  openColumnCategory: 'view:open:column-category',
  requestedByMeOwner: 'view:requested-by-me:owner',
} as const;

const OPEN_EXCLUDED_CATEGORIES = [
  Apollo.ColumnCategory.Done,
  Apollo.ColumnCategory.Cancelled,
] as const;

function getOpenColumnCategoryItem(): GridFilterItem {
  return {
    field: 'column-category',
    id: WORKFLOW_LIST_PRESET_FILTER_ITEM_ID.openColumnCategory,
    operator: 'isNoneOf',
    value: [...OPEN_EXCLUDED_CATEGORIES],
  };
}

function getCompletedColumnCategoryItem(): GridFilterItem {
  return {
    field: 'column-category',
    id: WORKFLOW_LIST_PRESET_FILTER_ITEM_ID.completedColumnCategory,
    operator: 'isAnyOf',
    value: [Apollo.ColumnCategory.Done],
  };
}

function getOwnerItem(currentUser: WorkflowListPresetUser): GridFilterItem {
  return {
    field: 'owner',
    id: WORKFLOW_LIST_PRESET_FILTER_ITEM_ID.requestedByMeOwner,
    operator: CustomOperatorValue.Owner,
    value: [currentUser],
  };
}

function getAssigneeItem(currentUser: WorkflowListPresetUser): GridFilterItem {
  return {
    field: 'permissions',
    id: WORKFLOW_LIST_PRESET_FILTER_ITEM_ID.assignedToMePermissions,
    operator: CustomOperatorValue.WorkflowAssigneeAnyOf,
    value: [currentUser],
  };
}

/**
 * Returns the selected preset’s full filter model. Personal presets resolve the
 * current user into owner / assignee values; without a user they fall back to Open.
 */
export function getWorkflowListPresetFilterModel({
  currentUser,
  presetId,
}: {
  currentUser: WorkflowListPresetUser | null;
  presetId: WorkflowListPresetId;
}): GridFilterModel {
  switch (presetId) {
    case 'open':
      return { items: [getOpenColumnCategoryItem()] };
    case 'requested-by-me':
      if (!currentUser) return { items: [getOpenColumnCategoryItem()] };
      return { items: [getOpenColumnCategoryItem(), getOwnerItem(currentUser)] };
    case 'assigned-to-me':
      if (!currentUser) return { items: [getOpenColumnCategoryItem()] };
      return { items: [getOpenColumnCategoryItem(), getAssigneeItem(currentUser)] };
    case 'completed':
      return { items: [getCompletedColumnCategoryItem()] };
    case 'all':
      return { items: [] };
  }
}

export function getIsWorkflowListPresetId(value: unknown): value is WorkflowListPresetId {
  return typeof value === 'string' && WORKFLOW_LIST_PRESET_IDS.some(id => id === value);
}

/** Persisted Workflow View state is unvalidated JSON, so narrow before handing it to the grid. */
export function getIsGridFilterModel(value: unknown): value is GridFilterModel {
  if (typeof value !== 'object' || value === null) return false;
  return Array.isArray(Reflect.get(value, 'items'));
}

export function getIsGridSortModel(value: unknown): value is GridSortModel {
  if (!Array.isArray(value)) return false;
  return value.every(item => {
    if (typeof item !== 'object' || item === null) return false;
    return typeof Reflect.get(item, 'field') === 'string';
  });
}

export function getWorkflowViewsTriggerLabel({
  activeWorkflowViewName,
  workflowListPreset,
}: {
  activeWorkflowViewName: string | null;
  workflowListPreset: WorkflowListPresetId;
}): string {
  if (activeWorkflowViewName) return activeWorkflowViewName;
  return WORKFLOW_LIST_PRESET_DEFINITIONS[workflowListPreset].label;
}

/** Views opted out of Saved Views stay out of the picker. */
export function getSavedWorkflowViewsForPicker<
  T extends {
    showInSavedViews: boolean;
  },
>(views: readonly T[]): T[] {
  return views.filter(view => view.showInSavedViews);
}
